import { useMemo } from "react";
import { Box, FormLabel } from "@mui/material";
import { useFormik } from "formik";
import CustomInput from "../../../../common/components/CustomInput";
import { ValidationError } from "../../../../common/hooks/usePydanticErrorsFormat";
import { Action, Pricing } from "../../pricingListSlice";
import PricingModal from "../PricingModal";

interface SetBookedManuallyModalProps {
	open: boolean;
	onClose: () => void;
	forPricings: Pricing[];
	onSubmit: (body: Record<string, number | Record<string, number>>) => void;
	isLoading: boolean;
	action?: Action | null;
	errorMessage: string | ValidationError | undefined | null;
}

interface FormValues {
	contract_id: string;
	contract_ids: Record<string, string>;
}

function getParties(pricings: Pricing[]) {
	interface PartyContractData {
		partyId: number;
		name: string;
		contractIds: number[];
	}

	const partiesById = new Map<number, PartyContractData>();

	pricings.forEach((pricing) => {
		const pricingParties = pricing.parties?.length
			? pricing.parties
			: pricing.party
			? [
					{
						party: pricing.party,
						party_id: pricing.party.party_id,
						geco_contract_id: pricing.geco_contract_id,
					},
			  ]
			: [];

		pricingParties.forEach((pricingParty) => {
			const partyId =
				pricingParty.party_id ?? pricingParty.party?.party_id;
			if (typeof partyId !== "number") {
				return;
			}

			const party: PartyContractData = partiesById.get(partyId) ?? {
				partyId,
				name:
					pricingParty.party?.short_name ||
					pricingParty.party?.name ||
					`Party ${partyId}`,
				contractIds: [],
			};
			if (
				pricingParty.geco_contract_id != null &&
				!party.contractIds.includes(pricingParty.geco_contract_id)
			) {
				party.contractIds.push(pricingParty.geco_contract_id);
			}
			partiesById.set(partyId, party);
		});
	});

	return Array.from(partiesById.values()).map((party) => ({
		...party,
		contractId:
			party.contractIds.length === 1 ? party.contractIds[0] : undefined,
	}));
}

export function SetBookedManuallyModal(props: SetBookedManuallyModalProps) {
	const {
		open,
		onClose,
		forPricings,
		onSubmit,
		isLoading,
		action,
		errorMessage,
	} = props;
	const parties = useMemo(() => getParties(forPricings), [forPricings]);
	const initialValues = useMemo<FormValues>(
		() => ({
			contract_id: "",
			contract_ids: Object.fromEntries(
				parties.map((party) => [
					party.partyId,
					party.contractId?.toString() ?? "",
				])
			),
		}),
		[parties]
	);
	const formik = useFormik<FormValues>({
		initialValues,
		enableReinitialize: true,
		validate: (values) => {
			if (!parties.length) {
				const id = Number(values.contract_id);
				return Number.isInteger(id) && id > 0
					? {}
					: { contract_id: "Enter a positive contract ID" };
			}

			const contractErrors: Record<string, string> = {};
			parties.forEach(({ partyId }) => {
				const id = Number(values.contract_ids[partyId]);
				if (!Number.isInteger(id) || id <= 0) {
					contractErrors[partyId] = "Enter a positive contract ID";
				}
			});
			return Object.keys(contractErrors).length
				? { contract_ids: contractErrors }
				: {};
		},
		onSubmit: (values) => {
			if (!parties.length) {
				onSubmit({ contract_id: Number(values.contract_id) });
				return;
			}
			onSubmit({
				contract_ids: Object.fromEntries(
					Object.entries(values.contract_ids).map(
						([partyId, contractId]) => [partyId, Number(contractId)]
					)
				),
			});
		},
	});

	return (
		<PricingModal
			open={open}
			onClose={onClose}
			onSubmit={() => formik.submitForm()}
			forPricings={forPricings}
			title={action?.display_name || "Set as manually booked"}
			subTitle={"Applicable for the following pricings:"}
			isSubmitDisabled={isLoading}
			isLoading={isLoading}
			errorMessage={errorMessage}
		>
			<form onSubmit={formik.handleSubmit}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					{parties.length ? (
						parties.map((party) => {
							const fieldName = `contract_ids.${party.partyId}`;
							return (
								<Box
									key={party.partyId}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
									}}
								>
									<FormLabel sx={{ width: "250px" }}>
										{party.name}
									</FormLabel>
									<CustomInput
										data-testid={`action-field-contract_id-${party.partyId}`}
										InputProps={{ type: "number" }}
										name={fieldName}
										placeholder="Contract Id"
										fullWidth
										onChange={formik.handleChange}
										value={
											formik.values.contract_ids[
												party.partyId
											] ?? ""
										}
										errorText={
											(
												formik.errors.contract_ids as
													| Record<string, string>
													| undefined
											)?.[party.partyId]
										}
										touched={true}
									/>
								</Box>
							);
						})
					) : (
						<CustomInput
							data-testid="action-field-contract_id"
							InputProps={{ type: "number" }}
							name="contract_id"
							placeholder="Contract Id"
							fullWidth
							onChange={formik.handleChange}
							value={formik.values.contract_id}
							errorText={formik.errors.contract_id}
							touched={true}
						/>
					)}
				</Box>
			</form>
		</PricingModal>
	);
}
